import { QueryObserverResult, useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { AgentFinancialResponse } from '../../api/events-client/events-client.type';
import {
  bulkUpsertSpending,
  getAgentCosts,
  getAllocationOfCostsChartData,
  getCostByInitiativeChartData,
  getCostVsOutputChartData,
  getSpending,
} from '../../api/financials-client/financials-client';
import type {
  AllocationOfCostsChartResponseByCategory,
  CostByInitiativeResponse,
  CostVsBudgetChartResponse,
  Spending,
  SpendingDetail,
} from '../../api/financials-client/financials-client.type';
import { getProjectsByPortfolioId } from '../../api/portfolio-client/portfolio-client';
import type { Project } from '../../api/projects-client/projects-client.type';
import { useFinancialsStore } from '../../store/financials-store/financials-store';
import { useFinancialsStoreHydration } from '../../store/financials-store/use-financials-store-hydration';
import { useGlobalStore } from '../../store/global-store/global-store';

/**
 * Get the spending data
 *
 * @param {string | undefined} portfolioId - The portfolio ID
 * @returns The spending data
 */
function useSpending(portfolioId: string | undefined): {
  data: Spending[] | undefined;
  query: QueryObserverResult<Spending[]>;
} {
  const query = useQuery({
    queryKey: ['getSpending', portfolioId],
    queryFn: async () => {
      return portfolioId ? getSpending(portfolioId) : Promise.reject('Missing Portfolio ID');
    },
    enabled: Boolean(portfolioId),
  });

  return { data: (query.data || []).filter((entry: any) => entry.subproject_id !== null), query };
}

/**
 * Use the save spending mutation
 *
 * @param payload - The payload to save the spending
 * @returns The mutation result
 */
function useSaveSpendingMutation(): UseMutationResult<
  Spending[],
  Error,
  { portfolio_id: string; year: number; projects: Record<string, Record<string, SpendingDetail[]>> },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      portfolio_id: string;
      year: number;
      projects: Record<string, Record<string, SpendingDetail[]>>;
    }) => {
      return bulkUpsertSpending(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getSpending'] });
    },
  });
}

/**
 * Get the cost by initiative chart data
 *
 * @param {Date | null} startDate - The start date
 * @param {Date | null} endDate - The end date
 * @param {string | null} portfolioId - The portfolio ID
 * @param {string | null} teamId - The team ID
 * @param {string | null} boardId - The board ID
 * @param {boolean} enabled - Optional enabled flag
 * @returns {object} Object containing the cost by initiative chart data and query result
 */
function useCostByInitiative(
  startDate: Date | null,
  endDate: Date | null,
  portfolioId: string | null,
  teamId: string | null,
  boardId: string | null,
  enabled = true,
): { data: CostByInitiativeResponse[] | undefined; query: QueryObserverResult<CostByInitiativeResponse[]> } {
  const superuserMode = useGlobalStore((state) => state.superuserMode);
  const forceRecalc = superuserMode.enabled ? superuserMode.forceRecalculation : false;
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const hasHydrated = useFinancialsStoreHydration();
  const workPeriodIds = useFinancialsStore((state) => state.workPeriodIds);

  const workPeriodIdsString = workPeriodIds.length > 0 ? workPeriodIds.join(',') : undefined;

  const query = useQuery<CostByInitiativeResponse[]>({
    queryKey: [
      'getCostByInitiativeData',
      startDate,
      endDate,
      portfolioId,
      teamId,
      boardId,
      workPeriodIdsString,
      forceRecalc,
      organizationId,
    ] as const,
    queryFn: async () => {
      if (!portfolioId || !startDate || !endDate || !organizationId) {
        throw new Error('Missing required parameters');
      }
      return await getCostByInitiativeChartData(
        portfolioId,
        startDate,
        endDate,
        teamId || undefined,
        boardId || undefined,
        workPeriodIdsString,
        forceRecalc,
        organizationId,
      );
    },
    enabled: enabled && hasHydrated && !!(portfolioId && startDate && endDate && organizationId),
  });

  return { data: query.data, query };
}

/**
 * Get the cost versus output chart data
 *
 * @param {Date | null} startDate - The start date
 * @param {Date | null} endDate - The end date
 * @param {string | null} portfolioId - The portfolio ID
 * @param {string | null} teamId - The team ID
 * @param {string | null} boardId - The board ID
 * @param {boolean} enabled - Optional enabled flag
 * @returns The cost versus output chart data
 */
function useCostVersusOutput(
  startDate: Date | null,
  endDate: Date | null,
  portfolioId: string | null,
  teamId: string | null,
  boardId: string | null,
  enabled = true,
): { data: CostVsBudgetChartResponse | undefined; query: QueryObserverResult<CostVsBudgetChartResponse> } {
  const superuserMode = useGlobalStore((state) => state.superuserMode);
  const forceRecalc = superuserMode.enabled ? superuserMode.forceRecalculation : false;
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const hasHydrated = useFinancialsStoreHydration();
  const workPeriodIds = useFinancialsStore((state) => state.workPeriodIds);

  const workPeriodIdsString = workPeriodIds.length > 0 ? workPeriodIds.join(',') : undefined;

  const query = useQuery<CostVsBudgetChartResponse>({
    queryKey: [
      'getCostVsOutputChartData',
      startDate,
      endDate,
      portfolioId,
      teamId,
      boardId,
      workPeriodIdsString,
      forceRecalc,
      organizationId,
    ],
    queryFn: async () => {
      if (!portfolioId || !startDate || !endDate || !organizationId) {
        throw new Error('Missing required parameters');
      }

      return getCostVsOutputChartData(
        startDate,
        endDate,
        portfolioId,
        teamId || undefined,
        boardId || undefined,
        workPeriodIdsString,
        forceRecalc,
        organizationId,
      );
    },
    enabled: enabled && hasHydrated && !!(portfolioId && startDate && endDate && organizationId),
  });

  return { data: query.data, query };
}

/**
 * Get the allocation of costs chart data
 *
 * @param {Date | null} startDate - The start date
 * @param {Date | null} endDate - The end date
 * @param {string | null} portfolioId - The portfolio ID
 * @param {string | null} teamId - The team ID
 * @param {string | null} subprojectId - The subproject ID
 * @param {boolean} enabled - Optional enabled flag
 * @returns {object} Object containing the allocation of costs chart data and query result
 */
function useAllocationOfCosts(
  startDate: Date | null,
  endDate: Date | null,
  portfolioId: string | null,
  teamId: string | null,
  subprojectId: string | null,
  enabled = true,
): {
  data: AllocationOfCostsChartResponseByCategory | undefined;
  query: QueryObserverResult<AllocationOfCostsChartResponseByCategory>;
} {
  const superuserMode = useGlobalStore((state) => state.superuserMode);
  const forceRecalc = superuserMode.enabled ? superuserMode.forceRecalculation : false;
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const hasHydrated = useFinancialsStoreHydration();
  const workPeriodIds = useFinancialsStore((state) => state.workPeriodIds);

  const workPeriodIdsString = workPeriodIds.length > 0 ? workPeriodIds.join(',') : undefined;

  const query = useQuery<AllocationOfCostsChartResponseByCategory>({
    queryKey: [
      'getAllocationOfCosts',
      startDate,
      endDate,
      portfolioId,
      teamId,
      subprojectId,
      workPeriodIdsString,
      forceRecalc,
      organizationId,
    ],
    queryFn: async () => {
      if (!portfolioId || !startDate || !endDate || !organizationId) {
        throw new Error('Missing required parameters');
      }
      return getAllocationOfCostsChartData(
        portfolioId,
        startDate,
        endDate,
        teamId,
        subprojectId,
        workPeriodIdsString,
        forceRecalc,
        organizationId,
      );
    },
    enabled: enabled && hasHydrated && !!(portfolioId && startDate && endDate && organizationId),
  });

  return { data: query.data, query };
}

/**
 * Get projects and boards for a portfolio
 *
 * @param {string | undefined} portfolioId - The portfolio ID
 * @returns The portfolio projects and boards
 */
function usePortfolioProjectsAndBoards(portfolioId: string | undefined): {
  data: Project[] | undefined;
  query: QueryObserverResult<Project[]>;
} {
  const query = useQuery({
    queryKey: ['getPortfolioProjectsAndBoards', portfolioId],
    queryFn: async () => {
      return portfolioId
        ? getProjectsByPortfolioId(portfolioId, ['id', 'name', 'subprojects'])
        : Promise.reject('Missing Portfolio ID');
    },
    enabled: Boolean(portfolioId),
  });
  const filteredAndSorted = useMemo(() => {
    const projects = query.data ?? [];
    return projects.filter((project) => project.subprojects.length > 0).sort((a, b) => a.name.localeCompare(b.name));
  }, [query.data]);

  return { data: filteredAndSorted, query };
}

/**
 * Get the agent costs data
 *
 * @param {Date | null} startDate - The start date
 * @param {Date | null} endDate - The end date
 * @param {string | null} portfolioId - The portfolio ID
 * @param {boolean} enabled - Optional enabled flag
 * @returns {object} Object containing the agent costs data and query result
 */
function useAgentCosts(
  startDate: Date | null,
  endDate: Date | null,
  portfolioId: string | null,
  enabled = true,
): { data: AgentFinancialResponse | undefined; query: QueryObserverResult<AgentFinancialResponse> } {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const hasHydrated = useFinancialsStoreHydration();

  const query = useQuery<AgentFinancialResponse>({
    queryKey: ['getAgentCosts', startDate, endDate, portfolioId, organizationId],
    queryFn: async () => {
      if (!startDate || !endDate || !organizationId) {
        throw new Error('Missing required parameters');
      }
      return await getAgentCosts(organizationId, startDate, endDate, portfolioId);
    },
    enabled: enabled && hasHydrated && !!(startDate && endDate && organizationId),
  });

  return { data: query.data, query };
}

export {
  useAgentCosts,
  useAllocationOfCosts,
  useCostByInitiative,
  useCostVersusOutput,
  usePortfolioProjectsAndBoards,
  useSaveSpendingMutation,
  useSpending,
};
